/**
 * referentielAM.js — Référentiel officiel du permis AM (source : Codes Rousseau Formateur).
 * Structure : 5 séquences pédagogiques obligatoires × leurs sous-objectifs A/B/C…
 * Chaque sous-objectif est mappé vers un ou plusieurs chapitres du cours existant dans l'app.
 *
 * Utilisé par :
 *  - /permis-am/programme (page publique — vue programme officiel)
 *  - Les cours J1 / J2 / Sensibilisation (badge visuel "S4.G" sur chaque section)
 */

/**
 * Mapping inverse : sectionId → refCode
 * Utilisé par les pages de cours pour afficher le badge officiel sur chaque section.
 * Format : { [slug]: { [numSection]: 'S#.LETTRE' } }
 */
export const SECTION_TO_REF = {
  'permis-am-journee-1': {
    '01': 'S1.A/B/C',   // Échanges initiaux (respect règles, thématiques, influence)
    '02': 'S2.A',       // Équipements indispensables
    '03': 'S2.B',       // Principaux organes du véhicule
    '04': 'S2.D',       // Installation — maîtrise du véhicule
    '05': 'S2.D',       // Démarrer / arrêter
    '06': 'S2.D',       // Équilibre
    '07': 'S2.D',       // Ralentir / freiner
    '08': 'S2.D',       // Tourner
    '09': 'S2.D',       // Éviter
    '10': 'S2.D',       // Enchaîner
    '11': 'S3.A/B/C/D', // Apports théoriques (signalisation, règles, positionnement, priorités)
    '12': 'S4',         // Cadre radio (préparation circulation)
    '13': 'S4',         // Premiers km en circulation
    '14': 'S1.C',       // Bilan / échanges
  },
  'permis-am-journee-2': {
    '01': 'S1',         // Bilan point de départ (représentations)
    '02': 'S2.C',       // Vérifications équipement/scooter (contrôles maintien sécurité)
    '03': 'S4.A',       // Réactivation démarrage
    '04': 'S4.A/H',     // Insertion (démarrer dans circulation + position chaussée)
    '05': 'S4.D',       // Regard étendu — indices utiles
    '06': 'S4.E',       // Adapter l'allure
    '07': 'S4.I',       // Franchir intersections
    '08': 'S4.I',       // Giratoires (type d'intersection)
    '09': 'S5.F',       // Usagers vulnérables
    '10': 'S4.D',       // Zones masquées — indices
    '11': 'S4.D',       // Lire les autres véhicules
    '12': 'S4.J',       // Communication / changer de direction
    '13': 'S4',         // Autonomie de conduite
    '14': 'S5',         // Sensibilisation risques (transition)
  },
  'permis-am-sensibilisation': {
    '01': 'S5.A',       // Comprendre les accidents
    '02': 'S5.A',       // Vulnérabilité
    '03': 'S5.E',       // Vitesse et conséquences
    '04': 'S5.A',       // Distracteurs / téléphone (risque spécifique)
    '05': 'S5.D',       // Produits psychoactifs
    '06': 'S5.A',       // Fatigue (risque spécifique)
    '07': 'S5.G',       // Pression des pairs
    '08': 'S5.A',       // Équipement complet (risque si absent)
    '09': 'S5.A',       // Passager (risque spécifique)
    '10': 'S5.B',       // Vérifications avant départ / entretien
    '11': 'S5.C',       // Débridage
    '12': 'S5.F',       // Reconnaître situation qui se dégrade (usagers vulnérables + soi)
    '13': 'S1.C',       // Échange famille (représentations / influence)
    '14': 'S5.A',       // Plan B (renoncer = décider)
    '15': 'S1',         // Engagement personnel (représentations)
  },
};

/**
 * Palette officielle des séquences (respecte les couleurs du Codes Rousseau)
 */
const COLORS = {
  S1: { name: 'blue',    label: 'Représentations',      hue: 200, bg: 'from-blue-600/25 to-blue-950/40',       border: 'border-blue-500/50',    text: 'text-blue-300',    solid: 'bg-blue-600' },
  S2: { name: 'pink',    label: 'Hors circulation',     hue: 330, bg: 'from-pink-600/25 to-pink-950/40',       border: 'border-pink-500/50',    text: 'text-pink-300',    solid: 'bg-pink-600' },
  S3: { name: 'amber',   label: 'Apports théoriques',   hue: 42,  bg: 'from-amber-600/25 to-amber-950/40',     border: 'border-amber-500/50',   text: 'text-amber-300',   solid: 'bg-amber-600' },
  S4: { name: 'emerald', label: 'Circulation',          hue: 150, bg: 'from-emerald-600/25 to-emerald-950/40', border: 'border-emerald-500/50', text: 'text-emerald-300', solid: 'bg-emerald-600' },
  S5: { name: 'purple',  label: 'Sensibilisation',      hue: 275, bg: 'from-purple-600/25 to-purple-950/40',   border: 'border-purple-500/50',  text: 'text-purple-300',  solid: 'bg-purple-600' },
};

export const REF_COLORS = COLORS;

/**
 * Le référentiel officiel complet — 5 séquences × leurs objectifs.
 * Chaque objectif liste les chapitres de l'app qui le couvrent.
 * Format link : { slug: 'permis-am-journee-1', section: '02', label: 'J1 §02' }
 */
export const REFERENTIEL_AM = [
  {
    code: 'S1',
    title: 'Représentations individuelles autour de la conduite',
    tagline: 'Ce que tu penses de la conduite AVANT de commencer',
    color: COLORS.S1,
    duree: 'Intégré à chaque séance',
    objectifs: [
      {
        code: 'A',
        title: 'Expression sur le respect des règles',
        desc: "Pourquoi les règles existent, ce qu'elles protègent, ce que je ressens face à elles.",
        links: [
          { slug: 'permis-am-journee-1', section: '01', label: 'J1 §01 · Avant de conduire, nous parlons de toi' },
        ],
      },
      {
        code: 'B',
        title: 'Expression sur les grandes thématiques de sécurité routière',
        desc: 'Vitesse, alcool, distracteurs, vulnérabilité — ce que je sais déjà, ce que je crois savoir.',
        links: [
          { slug: 'permis-am-journee-1', section: '01', label: 'J1 §01 · Représentations initiales' },
          { slug: 'permis-am-sensibilisation', section: '01', label: 'Sens §01 · Comprendre les accidents' },
        ],
      },
      {
        code: 'C',
        title: "Interaction entre le comportement des autres et le sien (influence et pression)",
        desc: "Comment les copains, la famille, les réseaux influencent mes choix au guidon.",
        links: [
          { slug: 'permis-am-journee-1', section: '01', label: 'J1 §01 · Influences' },
          { slug: 'permis-am-journee-1', section: '14', label: 'J1 §14 · Bilan / échanges' },
          { slug: 'permis-am-sensibilisation', section: '13', label: 'Sens §13 · Échange famille' },
        ],
      },
    ],
  },
  {
    code: 'S2',
    title: 'Formation à la conduite hors circulation',
    tagline: 'Plateau · maîtriser le véhicule avant de sortir',
    color: COLORS.S2,
    duree: '≈ 3 h de la formation',
    objectifs: [
      {
        code: 'A',
        title: 'Connaître les équipements indispensables et leurs rôles',
        desc: 'Casque, gants, blouson, chaussures — ce qui est obligatoire, ce qui est recommandé, pourquoi.',
        links: [
          { slug: 'permis-am-journee-1', section: '02', label: 'J1 §02 · Équipement' },
        ],
      },
      {
        code: 'B',
        title: 'Connaître les principaux organes du véhicule',
        desc: 'Commandes, freins, feux, clignotants, avertisseur, tableau de bord.',
        links: [
          { slug: 'permis-am-journee-1', section: '03', label: 'J1 §03 · Découverte du cyclomoteur' },
        ],
      },
      {
        code: 'C',
        title: "Contrôles indispensables à l'entretien et au maintien de la sécurité",
        desc: 'Pneus, freins, feux, huile, éclairage — le check-list avant chaque départ.',
        links: [
          { slug: 'permis-am-journee-2', section: '02', label: 'J2 §02 · Pré-conduite' },
          { slug: 'permis-am-sensibilisation', section: '10', label: 'Sens §10 · Vérifications avant départ' },
        ],
      },
      {
        code: 'D',
        title: 'Maîtriser son véhicule hors circulation',
        desc: 'Installation, démarrage, équilibre, freinage, virages, évitement, enchaînement.',
        links: [
          { slug: 'permis-am-journee-1', section: '04', label: 'J1 §04 · Installation' },
          { slug: 'permis-am-journee-1', section: '05', label: 'J1 §05 · Démarrer / arrêter' },
          { slug: 'permis-am-journee-1', section: '06', label: 'J1 §06 · Équilibre' },
          { slug: 'permis-am-journee-1', section: '07', label: 'J1 §07 · Ralentir / freiner' },
          { slug: 'permis-am-journee-1', section: '08', label: 'J1 §08 · Tourner' },
          { slug: 'permis-am-journee-1', section: '09', label: 'J1 §09 · Éviter' },
          { slug: 'permis-am-journee-1', section: '10', label: 'J1 §10 · Enchaîner' },
        ],
      },
    ],
  },
  {
    code: 'S3',
    title: 'Apports théoriques',
    tagline: 'Les règles clés à connaître avant la route',
    color: COLORS.S3,
    duree: '≈ 30 min · en salle',
    objectifs: [
      {
        code: 'A',
        title: 'Connaître la signalisation et comprendre son rôle',
        desc: 'Panneaux, hiérarchie, marquages au sol — comprendre ce que chaque signal impose.',
        links: [
          { slug: 'permis-am-journee-1', section: '11', label: 'J1 §11 · Théorie signalisation' },
        ],
      },
      {
        code: 'B',
        title: 'Connaître les principales règles de circulation et leur rôle',
        desc: 'Sens, vitesse, dépassement, arrêt/stationnement — pourquoi et pas seulement quoi.',
        links: [
          { slug: 'permis-am-journee-1', section: '11', label: 'J1 §11 · Règles de circulation' },
        ],
      },
      {
        code: 'C',
        title: 'Connaître les règles concernant le positionnement du véhicule',
        desc: "1/3 droit hors agglo, centre/1/3 gauche en agglo — où et pourquoi.",
        links: [
          { slug: 'permis-am-journee-1', section: '11', label: 'J1 §11 · Positionnement' },
          { slug: 'permis-am-journee-2', section: '04', label: 'J2 §04 · Insertion / position' },
        ],
      },
      {
        code: 'D',
        title: "Franchissement d'intersections et régimes de priorité",
        desc: 'Priorité à droite, cédez-le-passage, stop, feux, giratoire — qui passe avant qui.',
        links: [
          { slug: 'permis-am-journee-1', section: '11', label: 'J1 §11 · Intersections & priorités' },
          { slug: 'permis-am-journee-2', section: '07', label: 'J2 §07 · Intersections en réel' },
          { slug: 'permis-am-journee-2', section: '08', label: 'J2 §08 · Giratoires' },
        ],
      },
    ],
  },
  {
    code: 'S4',
    title: 'Formation à la conduite sur les voies ouvertes à la circulation publique',
    tagline: 'Circulation réelle · les 10 compétences à valider',
    color: COLORS.S4,
    duree: '≈ 3 h de la formation',
    objectifs: [
      { code: 'A', title: 'Démarrer le cyclomoteur',                                            desc: "Qu'il soit déjà ou non dans la circulation.",                                    links: [{ slug: 'permis-am-journee-2', section: '03', label: 'J2 §03 · Réactivation démarrage' }, { slug: 'permis-am-journee-2', section: '04', label: 'J2 §04 · Insertion' }] },
      { code: 'B', title: 'Ralentir et immobiliser le cyclomoteur',                             desc: 'Dans la circulation ou pour la quitter.',                                        links: [{ slug: 'permis-am-journee-1', section: '07', label: 'J1 §07 · Ralentir / freiner' }] },
      { code: 'C', title: "S'arrêter et stationner",                                            desc: 'Choisir un endroit sûr, immobiliser correctement, béquille.',                     links: [{ slug: 'permis-am-journee-1', section: '05', label: 'J1 §05 · S\'arrêter' }] },
      { code: 'D', title: 'Rechercher les indices utiles',                                       desc: 'Regard porté loin, zones masquées, lire les autres véhicules.',                   links: [{ slug: 'permis-am-journee-2', section: '05', label: 'J2 §05 · Regard étendu' }, { slug: 'permis-am-journee-2', section: '10', label: 'J2 §10 · Ce qui est masqué' }, { slug: 'permis-am-journee-2', section: '11', label: 'J2 §11 · Lire les autres' }] },
      { code: 'E', title: "Adapter l'allure",                                                    desc: 'Vitesse ajustée au contexte, à la météo, au trafic.',                             links: [{ slug: 'permis-am-journee-2', section: '06', label: 'J2 §06 · Allure anticipée' }] },
      { code: 'F', title: 'Distances de sécurité latérales et longitudinales',                   desc: '2 secondes devant, marge latérale, en toutes circonstances.',                    links: [{ slug: 'permis-am-journee-2', section: '06', label: 'J2 §06 · Allure & distances' }] },
      { code: 'G', title: 'Négocier un virage et adopter une trajectoire sécurisée',             desc: 'Ralentir avant, regarder la sortie, trajectoire large-serrée-large.',            links: [{ slug: 'permis-am-journee-1', section: '08', label: 'J1 §08 · Tourner' }] },
      { code: 'H', title: 'Choisir la position sur la chaussée',                                 desc: '1/3 droit hors agglo, centre/1/3 gauche en agglo, éviter portières.',            links: [{ slug: 'permis-am-journee-2', section: '04', label: 'J2 §04 · Insertion / position' }] },
      { code: 'I', title: "Franchir les différents types d'intersections",                       desc: 'Priorité à droite, cédez-le-passage, stop, feux, giratoire.',                    links: [{ slug: 'permis-am-journee-2', section: '07', label: 'J2 §07 · Intersections' }, { slug: 'permis-am-journee-2', section: '08', label: 'J2 §08 · Giratoires' }] },
      { code: 'J', title: 'Changer de direction',                                                desc: 'Contrôles rétros, clignotant anticipé, trajectoire propre.',                     links: [{ slug: 'permis-am-journee-2', section: '12', label: 'J2 §12 · Communication' }] },
    ],
  },
  {
    code: 'S5',
    title: 'Sensibilisation aux risques',
    tagline: '2 h obligatoires · en présence des parents si mineur',
    color: COLORS.S5,
    duree: '2 h · Présence parentale obligatoire (mineur)',
    objectifs: [
      { code: 'A', title: 'Risques spécifiques cyclomoteur & accidents caractéristiques',        desc: 'Vulnérabilité, angles morts, portières, chutes basse vitesse, refus de priorité.', links: [{ slug: 'permis-am-sensibilisation', section: '01', label: 'Sens §01 · Comprendre les accidents' }, { slug: 'permis-am-sensibilisation', section: '02', label: 'Sens §02 · Vulnérabilité' }, { slug: 'permis-am-sensibilisation', section: '14', label: 'Sens §14 · Plan B' }] },
      { code: 'B', title: "Conséquences d'un défaut d'entretien sur sécurité et environnement",   desc: 'Freins usés, pneus lisses, échappement modifié — pollution atmo & sonore.',       links: [{ slug: 'permis-am-sensibilisation', section: '10', label: 'Sens §10 · Vérifications' }] },
      { code: 'C', title: 'Risques dus au débridage',                                             desc: 'Perte de garantie, moteur qui casse, vitesse hors des capacités du cyclomoteur.',  links: [{ slug: 'permis-am-sensibilisation', section: '11', label: 'Sens §11 · Débridage' }] },
      { code: 'D', title: 'Produits psychoactifs et conduite',                                   desc: 'Alcool, cannabis, médicaments — leurs effets sur le cerveau du jeune conducteur.', links: [{ slug: 'permis-am-sensibilisation', section: '05', label: 'Sens §05 · Psychoactifs' }] },
      { code: 'E', title: 'Vitesse et ses conséquences sur la conduite',                          desc: 'Distance de freinage, énergie cinétique, gestion du regard, marge.',              links: [{ slug: 'permis-am-sensibilisation', section: '03', label: 'Sens §03 · Vitesse' }] },
      { code: 'F', title: 'Prise en compte des autres usagers vulnérables',                       desc: 'Piétons, cyclistes, personnes âgées, enfants — je suis vulnérable ET responsable.', links: [{ slug: 'permis-am-journee-2', section: '09', label: 'J2 §09 · Usagers vulnérables' }, { slug: 'permis-am-sensibilisation', section: '12', label: 'Sens §12 · Situation qui se dégrade' }] },
      { code: 'G', title: 'Influence et pression des pairs sur le futur conducteur',              desc: 'Copains, réseaux, image, prise de risque pour se conformer au groupe.',           links: [{ slug: 'permis-am-sensibilisation', section: '07', label: 'Sens §07 · Pression des copains' }] },
    ],
  },
];

/**
 * Helper : récupérer la couleur d'un code de référence (ex "S4.G" → COLORS.S4)
 */
export function refColor(refCode) {
  if (!refCode) return null;
  const base = refCode.split('.')[0]; // "S4.G" → "S4"
  return COLORS[base] || null;
}

/**
 * Helper : récupérer le refCode d'une section d'un cours donné.
 */
export function getRefForSection(slug, sectionNum) {
  return SECTION_TO_REF[slug]?.[sectionNum] || null;
}

export default REFERENTIEL_AM;
